// Miky Motorsport - kariera: logika odemykani, medaile, postup (M4)
// Ciste funkce nad profilem (testovatelne ve Vitest, bez Rapier/DOM).
import { CAREER, championshipPoints } from './data/events.js';

// Medaile podle pozice v zavode (1=zlato, 2=stribro, 3=bronz, jinak zadna).
export function medalForPosition(pos) {
  if (pos === 1) return 'gold';
  if (pos === 2) return 'silver';
  if (pos === 3) return 'bronze';
  return null;
}

// Vysledek konkretniho eventu z profilu (nebo null).
export function eventResult(profile, eventId) {
  return profile?.career?.events?.[eventId] || null;
}

// Ktere stupne pozicove medaile hrac na eventu drzi. Pozicove medaile se VNORUJI:
// 1. misto = zlato (a tim i stribro+bronz), 2. = stribro+bronz, 3. = bronz.
// bestPos = nejlepsi dosazena pozice (null/0/neodjeto -> zadna). Vraci {gold,silver,bronze}.
// Pro "medailovou pyramidu" na vyberu (Trackmania-style cile, ne jen aktualni stav).
export function medalSlots(bestPos) {
  const p = Number.isFinite(bestPos) && bestPos > 0 ? bestPos : Infinity;
  return { gold: p <= 1, silver: p <= 2, bronze: p <= 3 };
}

// Zaznam vysledku - drzi NEJLEPSI dosazenou pozici (a z ni odvozenou medaili).
export function recordEventResult(profile, eventId, position) {
  if (!profile.career) profile.career = { events: {} };
  if (!profile.career.events) profile.career.events = {};
  const prev = profile.career.events[eventId];
  // Validace: nevalidni pozice (NaN/Infinity/<1) nesmi poskodit zaznam.
  // Math.min(NaN, x) === NaN by "zamrzlo" rekord napor; takovou pozici ignorujeme.
  if (!Number.isFinite(position) || position < 1) return prev || null;
  const bestPos = prev ? Math.min(prev.bestPos, position) : position;
  profile.career.events[eventId] = { bestPos, medal: medalForPosition(bestPos) };
  return profile.career.events[eventId];
}

// Serie je "dohrana" = vsechny eventy maji zaznam vysledku (dojete, i bez medaile).
export function isSeriesPlayed(profile, series) {
  return series.events.every((e) => !!eventResult(profile, e.id));
}

// Predchozi serie v poradi kariery (nebo null pro prvni).
function prevSeries(series) {
  const idx = CAREER.findIndex((s) => s.id === series.id);
  return idx > 0 ? CAREER[idx - 1] : null;
}

// Je serie odemcena? - prvni vzdy; dalsi po DOHRANI predchozi serie
// (zabavnejsi nez grind), NEBO kdyz ma hrac dost vykonne auto (PI gate jako alternativa).
export function isSeriesUnlocked(profile, series, playerPI = 0) {
  const prev = prevSeries(series);
  if (!prev) return true;
  if (isSeriesPlayed(profile, prev)) return true;
  return playerPI >= (series.minPI || 0);
}

// Je event v serii odemceny? - prvni vzdy (kdyz je serie odemcena),
// dalsi az po DOKONCENI predchoziho (existuje zaznam vysledku).
export function isEventUnlocked(profile, series, idx) {
  if (idx <= 0) return true;
  const prevEvent = series.events[idx - 1];
  return !!eventResult(profile, prevEvent.id);
}

// Souhrn stavu pro UI: serie -> {unlocked, events:[{unlocked, medal, bestPos}], medals, total}.
export function careerView(profile, playerPI) {
  return CAREER.map((s) => {
    const sUnlocked = isSeriesUnlocked(profile, s, playerPI);
    const events = s.events.map((e, i) => {
      const r = eventResult(profile, e.id);
      return {
        event: e,
        unlocked: sUnlocked && isEventUnlocked(profile, s, i),
        medal: r ? r.medal : null,
        bestPos: r ? r.bestPos : null,
        done: !!r,
      };
    });
    const medals = events.filter((e) => e.medal).length;
    return { series: s, unlocked: sUnlocked, events, medals, total: events.length };
  });
}

// Celkovy pocet medaili (a zlatych) napric karierou.
export function medalTally(profile) {
  let total = 0, gold = 0, silver = 0, bronze = 0;
  const ev = profile?.career?.events || {};
  for (const id in ev) {
    const m = ev[id].medal;
    if (!m) continue;
    total++;
    if (m === 'gold') gold++;
    else if (m === 'silver') silver++;
    else if (m === 'bronze') bronze++;
  }
  return { total, gold, silver, bronze };
}

// Souhrn "zlate sbirky" napric celou hrou (pro menu cil X/Y). Kariera (pozicova zlata) +
// time-attack (casova zlata, dodava volajici z timeMedalTally) + sampionatni tituly.
// timeGold/timeTotal jsou parametry, aby career.js nezavisel na tratich/timeMedals.
export function goldCollection(profile, timeGold = 0, timeTotal = 0) {
  const careerGold = medalTally(profile).gold;
  const careerTotal = CAREER.reduce((n, s) => n + s.events.length, 0);
  let champTitles = 0, champTotal = 0;
  for (const s of CAREER) {
    if (!s.championship) continue;
    champTotal++;
    if (isChampionshipWon(profile, s)) champTitles++;
  }
  const tg = Number.isFinite(timeGold) ? timeGold : 0;
  const tt = Number.isFinite(timeTotal) ? timeTotal : 0;
  return {
    gold: careerGold + tg + champTitles,
    total: careerTotal + tt + champTotal,
    careerGold, careerTotal, timeGold: tg, timeTotal: tt, champTitles, champTotal,
  };
}

// Je cela serie dotazena na medaile (vse aspon bronz)?
export function isSeriesComplete(profile, series) {
  return series.events.every((e) => {
    const r = eventResult(profile, e.id);
    return r && r.medal;
  });
}

// --- Sampionaty (serie s championship:true) - body se scitaji napric eventy serie ---
// Pristup: ulozime snapshot poradi VSECH ridicu z eventu a standings dopocitavame.
// To je idempotentni (opakovani eventu prepise jen ten event) a recomputovatelne.

// Ma se vysledek zavodu pocitat do sampionatu? Jen plne pole (>2 auta).
// Duel (hrac + 1 souper) by dal jednomu rivalovi body zadarmo a ostatni contendeni
// by za to kolo nedostali nic (nerovny pocet kol) - to zkresluje tabulku. Duel se proto
// do sampionatu nepocita (zustava bezny event s medaili/odmenou).
export function isFullField(results) {
  return Array.isArray(results) && results.length > 2;
}

// Je event sampionatni "bodovane kolo" = plne pole (vic nez 2 auta na startu)?
// Plne pole = hrac + numAI souperu > 2, tedy numAI >= 2. Stejna brana jako isFullField
// nad vysledky jizdy (results.length = numAI + 1) - duel (numAI 1, 2 auta) se nepocita.
// Pouziva se pro championshipProgress.total (kolik kol VUBEC muze pridat body), aby
// done === total slo dosahnout odjetim vsech bodovanych kol (duely se nikdy nezaznamenaji).
export function isFullFieldEvent(event) {
  return !!event && Number(event.numAI) + 1 > 2;
}

// Zaznam vysledku sampionatniho eventu - ulozi poradi vsech ridicu z jizdy hrace.
// Drzi snapshot z NEJLEPSI jizdy (dle pozice hrace), aby opakovani titul nezhorsilo
// (konzistentni s recordEventResult, ktery taky drzi nejlepsi).
// order = pole z race.results(): [{ name, position, isPlayer }, ...].
export function recordChampionshipResult(profile, series, eventId, order) {
  if (!series || !series.championship || !Array.isArray(order) || order.length === 0) return null;
  if (!profile.career) profile.career = { events: {} };
  if (!profile.career.championships) profile.career.championships = {};
  const champs = profile.career.championships;
  const champ = champs[series.id] || (champs[series.id] = { events: {} });
  // Validace pozice hrace: nevalidni (NaN/Infinity) -> sentinel 99 (nejhorsi),
  // jinak by playerPos===NaN navzdy blokovalo zlepseni (NaN<=x je vzdy false) a
  // ulozilo by NaN do rekordu.
  const rawPlayerPos = (order.find((o) => o.isPlayer) || {}).position;
  const playerPos = Number.isFinite(rawPlayerPos) ? rawPlayerPos : 99;
  const prev = champ.events[eventId];
  if (!prev || playerPos <= prev.playerPos) {
    champ.events[eventId] = {
      playerPos,
      order: order.map((o) => ({ name: o.name, position: o.position, isPlayer: !!o.isPlayer })),
    };
  }
  return champ.events[eventId];
}

// Countback porovnani dvou ridicu pri shode na bodech: vic prvnich mist vyhrava,
// pak vic druhych, atd. (zavodni "countback" pravidlo). finishes = pole pozic ridice.
// Vraci <0 kdyz a ma byt vyse, >0 kdyz b vyse, 0 pri uplne shode multisetu pozic.
function countback(a, b) {
  // pocty dokonceni na kazde pozici (1-based); porovnavame od nejlepsi pozice
  const ca = {}, cb = {};
  let maxPos = 0;
  for (const p of a.finishes) { ca[p] = (ca[p] || 0) + 1; if (p > maxPos) maxPos = p; }
  for (const p of b.finishes) { cb[p] = (cb[p] || 0) + 1; if (p > maxPos) maxPos = p; }
  for (let pos = 1; pos <= maxPos; pos++) {
    const diff = (cb[pos] || 0) - (ca[pos] || 0); // vic dokonceni na lepsi pozici = vyse
    if (diff !== 0) return diff;
  }
  return 0;
}

// Prubezne poradi sampionatu = soucet bodu kazdeho ridice pres zaznamenane eventy.
// Vraci sestupne serazene pole [{ name, points, isPlayer, rounds }].
// Tie-break: body -> countback (vic 1. mist, pak 2. ...) -> hrac vyhrava uplnou remizu
// (zde tied-for-first dostane titul, nikdy o nej neprijde kvuli poradi vlozeni).
export function championshipStandings(profile, series) {
  const champ = profile?.career?.championships?.[series?.id];
  const totals = {};
  if (champ && champ.events && typeof champ.events === 'object') {
    for (const eid in champ.events) {
      const entry = champ.events[eid];
      // robustnost proti poskozenym profilum: preskoc zaznam bez platneho pole `order`
      if (!entry || !Array.isArray(entry.order)) continue;
      for (const o of entry.order) {
        // preskoc polozky bez platneho jmena/pozice (nikdy nehazet)
        if (!o || typeof o.name !== 'string' || !Number.isFinite(o.position)) continue;
        const t = totals[o.name] || (totals[o.name] = { name: o.name, points: 0, isPlayer: false, rounds: 0, finishes: [] });
        t.points += championshipPoints(o.position);
        t.rounds += 1;
        t.finishes.push(o.position);
        if (o.isPlayer) t.isPlayer = true;
      }
    }
  }
  const rows = Object.values(totals);
  rows.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const cb = countback(a, b);
    if (cb !== 0) return cb;
    // uplna remiza (body i countback): hrac vyhrava, jinak stabilni dle jmena
    if (a.isPlayer !== b.isPlayer) return a.isPlayer ? -1 : 1;
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });
  rows.forEach((r) => { delete r.finishes; });
  return rows;
}

// Kolik eventu serie uz ma zaznam v sampionatu (X/Y odjeto).
// total = jen bodovana kola (plna pole) - duely se do sampionatu nikdy nezaznamenaji
// (viz isFullField/recording gate v game.js), takze pocitat je do total by drzelo
// progress navzdy na "n-1/n" a titul (done >= total) by nesel nikdy ziskat.
export function championshipProgress(profile, series) {
  const champ = profile?.career?.championships?.[series?.id];
  const done = champ ? Object.keys(champ.events).length : 0;
  const total = series ? series.events.filter(isFullFieldEvent).length : 0;
  return { done, total };
}

// Prubezna pozice hrace v sampionatu (1-based) nebo null (zadny zaznam).
// Vraci null i kdyz hrac sice figuruje, ale nema zadne odjete kolo (poskozena/rucni data) -
// neucastnik nedostane kladne poradi.
export function championshipPlayerRank(profile, series) {
  const rows = championshipStandings(profile, series);
  const i = rows.findIndex((r) => r.isPlayer);
  if (i < 0 || !(rows[i].rounds > 0)) return null;
  return i + 1;
}

// Je sampionat dohran a hrac je v cele prubezneho poradi (= titul)?
export function isChampionshipWon(profile, series) {
  if (!series || !series.championship) return false;
  const { done, total } = championshipProgress(profile, series);
  if (done < total) return false;
  const rows = championshipStandings(profile, series);
  return rows.length > 0 && rows[0].isPlayer;
}

// Prave timto zavodem hrac NOVE ziskal titul? = nebyl sampionem PRED zaznamem teto
// jizdy, ale po zaznamu uz je. wonBefore zachyti volajici (isChampionshipWon nad profilem
// PRED recordChampionshipResult), profil uz musi byt po zaznamu. Tim se oslava spusti jen
// na prechodu (ne na kazdem dalsim zavodu uz vyhrane serie). Cista, testovatelna logika.
export function newlyWonChampionship(wonBefore, profile, series) {
  return !wonBefore && isChampionshipWon(profile, series);
}

// Je kariera dokoncena timto zavodem? Cista verze pravidla z endRace.
//   allSeriesComplete = jsou VSECHNY serie kariery na medaile (isSeriesComplete)?
//   isFinaleWin       = vyhral hrac (pos 1) event s finale:true?
// Kariera je hotova kdyz jsou vsechny serie dotazene NEBO hrac vyhral finale (Grand Finále).
// Finale je zkratka k titulu i kdyz drivejsi serie nejsou na medaile - dramaticke zakonceni.
export function careerCompleted(allSeriesComplete, isFinaleWin) {
  return !!allSeriesComplete || !!isFinaleWin;
}

// Vyber zaverecne oslavne obrazovky po zavode (F-201). Cista rozhodovaci logika z endRace -
// drive strikni if/else-if, ktery pri vyhre Grand Finále (careerDone I wonChamp zaroven)
// spustil viteznou obrazovku kariery a TICHE polkl oslavu titulu Grand Championship. Protoze
// prechod titulu je jednorazovy (uz zaznamenan), replay uz titul nikdy znovu nespusti -> oslava
// Grand titulu byla navzdy nedosazitelna. Reseni: jeden overlay, ale vitezna obrazovka kariery
// titul UZNA (extra radek). Vraci deskriptor:
//   { kind: 'winner'|'champion'|'toast', acknowledgeChampionship: <serie|null> }
//   - careerDone           -> 'winner' (a kdyz hrac zaroven NOVE ziskal titul, acknowledge tu serii)
//   - jen wonChampSeries   -> 'champion'
//   - ani jedno            -> 'toast'
export function pickEndRaceCelebration({ careerDone, wonChampSeries } = {}) {
  if (careerDone) {
    // vitezna obrazovka kariery ma prednost (nedvojime dva overlaye), ale soubezne ziskany
    // titul uznáme jednim radkem, aby nezmizel bez povsimnuti (F-201).
    return { kind: 'winner', acknowledgeChampionship: wonChampSeries || null };
  }
  if (wonChampSeries) return { kind: 'champion', acknowledgeChampionship: wonChampSeries };
  return { kind: 'toast', acknowledgeChampionship: null };
}

// Dalsi doporuceny event (prvni odemceny bez zlata) - pro "pokracovat" tlacitko.
export function nextRecommendedEvent(profile, playerPI) {
  const view = careerView(profile, playerPI);
  for (const sv of view) {
    if (!sv.unlocked) continue;
    for (const ev of sv.events) {
      if (ev.unlocked && ev.medal !== 'gold') return ev.event.id;
    }
  }
  return null;
}
